import React from "react";

export default function OnboardingStepsSection() {
  const steps = [
    {
      number: 1,
      title: "Organisation Details",
      desc: "Facility & organisation name, facility type, ownership.",
    },
    {
      number: 2,
      title: "Contact Details",
      desc: "Contact person, email, mobile, address, GPS location.",
    },
    {
      number: 3,
      title: "Facility Capabilities",
      desc: "CNC, 3D printing, fabrication, electronics, robotics and more.",
    },
    {
      number: 4,
      title: "Machines & Equipment",
      desc: "Add each machine — model, capacity, materials, photos, availability.",
    },
    {
      number: 5,
      title: "Infrastructure",
      desc: "Facility area, team size, testing infrastructure, power capacity.",
    },
    {
      number: 6,
      title: "Certifications",
      desc: "GST, PAN, ISO, industry certifications and other documents.",
    },
    {
      number: 7,
      title: "Facility Showcase",
      desc: "Logo, cover, photos, videos, portfolio and case studies.",
    },
    {
      number: 8,
      title: "Review & Submit",
      desc: "Full profile preview. Submit for APIC verification.",
    },
  ];

  const ArrowIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );

  return (
    <section className="ob-section">
      <style>{`
        .ob-section {
          width: 100%;
          background: linear-gradient(180deg, #f7f9fd 0%, #eef1f8 100%);
          font-family: 'Inter', 'Segoe UI', sans-serif;
          box-sizing: border-box;
          padding: 72px 0;
        }
        .ob-wrap {
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 32px;
          box-sizing: border-box;
          text-align: center;
        }
        .ob-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 9px 18px;
          border-radius: 999px;
          background: #fdecd3;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #0b1330;
        }
        .ob-eyebrow-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: #f7941d; flex-shrink: 0;
        }
        .ob-heading {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 800;
          font-size: clamp(1.9rem, 4.2vw, 2.9rem);
          line-height: 1.2;
          letter-spacing: -0.02em;
          color: #0b1330;
          margin: 22px 0 0;
        }
        .ob-accent { color: #f7941d; }
        .ob-subheading {
          margin-top: 16px;
          font-size: clamp(0.95rem, 2vw, 1.08rem);
          line-height: 1.6;
          color: #5a6280;
          max-width: 640px;
          margin-left: auto;
          margin-right: auto;
        }

        .ob-grid {
          margin-top: 44px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          text-align: left;
        }
        .ob-card {
          background: #ffffff;
          border: 1px solid #e7eaf3;
          border-radius: 16px;
          padding: 24px 22px;
          box-shadow: 0 18px 36px -28px rgba(11,19,48,0.15);
          box-sizing: border-box;
        }
        .ob-number {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f7941d, #ef7f0e);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 800;
          font-size: 1rem;
          color: #1a1204;
          margin-bottom: 16px;
        }
        .ob-card-title {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 1.05rem;
          color: #0b1330;
          margin: 0 0 8px;
        }
        .ob-card-desc {
          font-size: 0.9rem;
          line-height: 1.55;
          color: #6b7290;
          margin: 0;
        }

        .ob-cta-row {
          margin-top: 40px;
          display: flex;
          justify-content: center;
          gap: 14px;
          flex-wrap: wrap;
        }
        .ob-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #0b1330;
          color: #ffffff;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 0.98rem;
          padding: 15px 26px;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          white-space: nowrap;
        }
        .ob-btn-secondary {
          background: #ffffff;
          color: #0b1330;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 0.98rem;
          padding: 15px 26px;
          border-radius: 12px;
          border: 1px solid #dfe3ee;
          cursor: pointer;
          white-space: nowrap;
        }

        /* Tablet: 2 columns */
        @media (max-width: 920px) {
          .ob-grid { grid-template-columns: repeat(2, 1fr); }
        }

        /* Phones: 1 column, tighter spacing, full-width buttons */
        @media (max-width: 560px) {
          .ob-section { padding: 48px 0; }
          .ob-wrap { padding: 0 20px; }
          .ob-grid { grid-template-columns: 1fr; margin-top: 32px; gap: 14px; }
          .ob-card { padding: 20px; }
          .ob-cta-row { flex-direction: column; margin-top: 32px; }
          .ob-btn-primary, .ob-btn-secondary {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      <div className="ob-wrap">
        <span className="ob-eyebrow">
          <span className="ob-eyebrow-dot" />
          REGISTRATION FLOW
        </span>

        <h2 className="ob-heading">
          Onboarding, <span className="ob-accent">step by step.</span>
        </h2>

        <p className="ob-subheading">
          A structured multi-step flow to capture everything an innovator
          needs to trust your facility.
        </p>

        <div className="ob-grid">
          {steps.map((s) => (
            <div className="ob-card" key={s.number}>
              <div className="ob-number">{s.number}</div>
              <h3 className="ob-card-title">{s.title}</h3>
              <p className="ob-card-desc">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="ob-cta-row">
          <button type="button" className="ob-btn-primary">
            Start Registration
            <ArrowIcon />
          </button>
          <button type="button" className="ob-btn-secondary">
            Facility Login
          </button>
        </div>
      </div>
    </section>
  );
}